import { IEntityFieldType } from '../../../types/entities/IEntityFieldType';
import {
    doubleMD_ColumnGrid,
    full_ColumnGrid,
} from '../../../utils/commonVariables/grid';
import { getRandomNumber } from '../../../utils/usefulFunctions';
import { TagColor } from '../../../muiComponents/Tag';
import {
    ActivityLogState,
    ActivityLogTypeKey,
    AgencyConnectionState,
    AgencyLogGroupState,
    AgencyTypeLogState,
    AttributeDefinitionFieldType,
    AttributeDefinitionSizeKeyType,
    AttributeDefinitionState,
    ComplementaryServiceCategoryState,
    ComplementaryServiceTypeState,
    ConnectivityLogsSortDirection,
    ConnectivityLogsSortKey,
    ConnectivityLogsSortState,
    HotelContentState,
    LocalHotelImageState,
    SuggestedURLState,
    URLAvailabilityStatus,
} from '../types/zitoneoServicesTypes';

export const HOTEL_CONTENT_INITIAL_STATE: HotelContentState = {
    Id: '',
    IdHotel: 0,
    Name: '',
    HotelOwner: '',
    Address: '',
    City: '',
    Country: '',
    GiataCode: '',
    LastChange: '',
    LastChangeBy: '',
    Rooms: 0,
    HotelServices: [],
    ContractType: {
        Name: '',
        Translations: [],
    },
    Category: '',
    CategoryKey: '',
    ZitoneoPublishStatus: '',
    ZitoneoPublishStatusKey: '',
    ZitoneoPublishStatusColor: '',
    URLs: [],
    ZitoneoMemberDate: '',
    ZitoneoMembership: true,
    HotelImages: [],
    HotelRoomTypes: [],
    ActivityLogs: [],
    IdLogoFile: 0,
    LogoFile: {
        Caption: '',
        VirtualPath: '',
    },
    IdIconFile: 0,
    IconFile: {
        Caption: '',
        VirtualPath: '',
    },
    ZitoneoScrapingResults: [],
};

export const toURLSlug = (text: string): string =>
    text
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

export const isAlreadyUsedPath = (
    path: string,
    allUsedPaths: string[],
    currentHotelPaths: string[]
): boolean => {
    return allUsedPaths.includes(path) || currentHotelPaths.includes(path);
};

export const getSuggestedURLs = (
    name: string,
    city: string,
    idHotel: number,
    allUsedPaths: string[],
    currentHotelPaths: string[]
): SuggestedURLState[] => {
    const slugName = toURLSlug(name);
    const slugCity = toURLSlug(city);
    const candidates = [
        slugName,
        slugCity ? `${slugName}-${slugCity}` : null,
        `${slugName}-${idHotel}`,
        slugCity ? `${slugName}-${slugCity}-${idHotel}` : null,
    ].filter(Boolean) as string[];
    return candidates
        .filter(c => !currentHotelPaths.includes(c))
        .map(c => ({
            path: c,
            status: allUsedPaths.includes(c)
                ? URLAvailabilityStatus.NOT_AVAILABLE
                : URLAvailabilityStatus.AVAILABLE,
        }));
};

export const getFieldType = (
    fieldType: Partial<IEntityFieldType>
): AttributeDefinitionFieldType => {
    switch (true) {
        case fieldType.IsBoolean:
            return AttributeDefinitionFieldType.BOOLEAN;
        case fieldType.IsDate:
            return AttributeDefinitionFieldType.DATE;
        case fieldType.IsNumeric:
            return AttributeDefinitionFieldType.NUMBER;
        default:
            return AttributeDefinitionFieldType.STRING;
    }
};

export const getGridChildSize = (fieldSize: AttributeDefinitionSizeKeyType) => {
    switch (fieldSize) {
        case AttributeDefinitionSizeKeyType.SMALL:
            return doubleMD_ColumnGrid;
        case AttributeDefinitionSizeKeyType.MEDIUM:
            return full_ColumnGrid;
        case AttributeDefinitionSizeKeyType.LARGE:
            return full_ColumnGrid;
        default:
            return doubleMD_ColumnGrid;
    }
};

export const getDefaultValue = (
    fieldType: AttributeDefinitionFieldType
): string | boolean | Date | number | null => {
    switch (fieldType) {
        case AttributeDefinitionFieldType.BOOLEAN:
            return 'False';
        case AttributeDefinitionFieldType.DATE:
            return new Date();
        case AttributeDefinitionFieldType.NUMBER:
            return 0;
        default:
            return '';
    }
};

const getFieldTypeSortOrder = (fieldType: AttributeDefinitionFieldType) => {
    switch (fieldType) {
        case AttributeDefinitionFieldType.BOOLEAN:
            return 0;
        case AttributeDefinitionFieldType.STRING:
            return 1;
        case AttributeDefinitionFieldType.NUMBER:
            return 2;
        case AttributeDefinitionFieldType.DATE:
            return 3;

        default:
            return 99;
    }
};

export const sortAttributeDefinitions = (
    attributeDefinitions: AttributeDefinitionState[]
) => {
    return [...attributeDefinitions].sort((a, b) => {
        if (a.Unique !== b.Unique) {
            return a.Unique ? -1 : 1;
        }

        const fieldTypeComparison =
            getFieldTypeSortOrder(a.FieldType) -
            getFieldTypeSortOrder(b.FieldType);

        if (fieldTypeComparison !== 0) {
            return fieldTypeComparison;
        }

        return a.Name.localeCompare(b.Name);
    });
};

export const createNewHotelService = (
    service: ComplementaryServiceTypeState,
    category: ComplementaryServiceCategoryState
) => {
    return {
        Id: `-${getRandomNumber()}`,
        IdServiceType: parseInt(service.Id),
        ServiceType: service.Name,
        IdServiceCategory: parseInt(category.Id) || 0,
        ServiceCategory: category.Name || '',
        Name: '',
        Description: '',
        Exceptions: [],
    };
};

export const isNewAndDeleted = (item: {
    Id: string;
    Deleted?: boolean;
    __isAdded?: boolean;
}) => (parseInt(item.Id) < 0 || item.__isAdded) && item.Deleted;

export const isNewWithoutValue = <
    T extends { Id: string; __isAdded?: boolean }
>(
    item: T,
    key: keyof T
) => (parseInt(item.Id) < 0 || item.__isAdded) && !item[key];

export const getIsAdded = (item: { Id: string; __isAdded?: boolean }) =>
    !!(parseInt(item.Id) < 0 || item.__isAdded);

export const getIsChanged = (item: {
    __isChanged?: boolean;
    Id: string;
    __isAdded?: boolean;
}) => item.__isChanged || parseInt(item.Id) > 0 || !item.__isAdded;

export const STATUS_COLORS = {
    NOT_STARTED: 'var(--grey-400)',
    DRAFT: 'var(--grey-700)',
    PUBLISHED: 'var(--primary-400)',
};

export const STATUS_TAG_COLORS: Record<keyof typeof STATUS_COLORS, TagColor> = {
    NOT_STARTED: 'grey',
    DRAFT: 'grey',
    PUBLISHED: 'green',
};

export const STATUS_KEYS = {
    NOT_STARTED: 'NOT_STARTED',
    DRAFT: 'DRAFT',
    PUBLISHED: 'PUBLISHED',
};

export const CRITICAL_EXCEPTION_LEVEL_TYPE_KEY = 'CRITICAL';

export const getNextPublishStatusKey = (currentStatusKey: string): string => {
    if (currentStatusKey === STATUS_KEYS.NOT_STARTED) {
        return STATUS_KEYS.DRAFT;
    }

    if (currentStatusKey === STATUS_KEYS.DRAFT) {
        return STATUS_KEYS.PUBLISHED;
    }

    if (currentStatusKey === STATUS_KEYS.PUBLISHED) {
        return STATUS_KEYS.DRAFT;
    }
    return STATUS_KEYS.DRAFT;
};

export const DEFAULT_CHARACTER_LIMIT = 2000;

export const ZITONEO_PUBLIC_WEBSITE_URL_HOTEL_DETAIL =
    process.env.REACT_APP_ZITONEO_PUBLIC_WEBSITE_URL + 'hotels/';

// --- Connectivity logs (Activity logs panel) ---

export const CONNECTIVITY_LOGS_RELEVANT_TYPES = [
    ActivityLogTypeKey.CATALOGUE,
    ActivityLogTypeKey.ERRATAS,
];

export const CONNECTIVITY_LOG_TYPE_ICONS: Record<ActivityLogTypeKey, string> = {
    [ActivityLogTypeKey.CATALOGUE]: 'List',
    [ActivityLogTypeKey.ERRATAS]: 'ErrorOutlineOutlined',
    [ActivityLogTypeKey.OTHER]: 'Info',
};

export const CONNECTIVITY_LOG_TYPE_TAG_COLORS: Record<
    ActivityLogTypeKey,
    TagColor
> = {
    [ActivityLogTypeKey.CATALOGUE]: 'grey',
    [ActivityLogTypeKey.ERRATAS]: 'orange',
    [ActivityLogTypeKey.OTHER]: 'blue',
};

export const CONNECTIVITY_LOGS_SORT_DEFAULT: ConnectivityLogsSortState = {
    key: ConnectivityLogsSortKey.LAST_CONNECTION,
    direction: ConnectivityLogsSortDirection.DESC,
};

export const CONNECTIVITY_LOGS_SORT_KEY_OPTIONS = [
    ConnectivityLogsSortKey.LAST_CONNECTION,
    ConnectivityLogsSortKey.AGENCY_NAME,
];

export const CONNECTIVITY_LOGS_SORT_KEY_LABELS: Record<
    ConnectivityLogsSortKey,
    string
> = {
    [ConnectivityLogsSortKey.AGENCY_NAME]: 'agency_name',
    [ConnectivityLogsSortKey.LAST_CONNECTION]: 'last_connection',
};

export const CONNECTIVITY_LOGS_SORT_DIRECTION_ICONS: Record<
    ConnectivityLogsSortDirection,
    string
> = {
    [ConnectivityLogsSortDirection.ASC]: 'ArrowUpward',
    [ConnectivityLogsSortDirection.DESC]: 'ArrowDownward',
};

// orderedActivityLogs debe venir ordenado de más reciente a más antiguo.
export const getAgencyConnectionStates = (
    orderedActivityLogs: ActivityLogState[]
): AgencyConnectionState[] => {
    const agencies = Array.from(
        new Set(
            orderedActivityLogs.filter(log => log.Agency).map(log => log.Agency)
        )
    );

    return agencies.map(agency => {
        const lastLog = orderedActivityLogs.find(log => log.Agency === agency);
        return {
            name: agency,
            failed: lastLog?.Failed || false,
            lastConnection: lastLog?.DateCreated || null,
        };
    });
};

// activityLogs debe venir ordenado de más reciente a más antiguo.
export const buildAgencyLogGroups = (
    activityLogs: ActivityLogState[]
): AgencyLogGroupState[] => {
    const agencies = Array.from(
        new Set(activityLogs.filter(log => log.Agency).map(log => log.Agency))
    );

    return agencies.map(agency => {
        const agencyLogs = activityLogs.filter(log => log.Agency === agency);

        const types = CONNECTIVITY_LOGS_RELEVANT_TYPES.reduce<
            AgencyTypeLogState[]
        >((acc, typeKey) => {
            const typeLogs = agencyLogs.filter(log => log.TypeKey === typeKey);
            const last = typeLogs[0];
            if (!last) return acc;

            const lastSuccessful = last.Failed
                ? typeLogs.find(log => !log.Failed)
                : undefined;

            acc.push({ typeKey, last, lastSuccessful });
            return acc;
        }, []);

        return {
            agency,
            types,
            lastConnection: agencyLogs[0]?.DateCreated
                ? new Date(agencyLogs[0].DateCreated)
                : null,
        };
    });
};

export const sortAgencyLogGroups = (
    agencyGroups: AgencyLogGroupState[],
    sortedBy: ConnectivityLogsSortState
): AgencyLogGroupState[] => {
    const directionMultiplier =
        sortedBy.direction === ConnectivityLogsSortDirection.ASC ? 1 : -1;

    return [...agencyGroups].sort((a, b) => {
        const comparison =
            sortedBy.key === ConnectivityLogsSortKey.AGENCY_NAME
                ? a.agency.localeCompare(b.agency)
                : (a.lastConnection?.getTime() || 0) -
                  (b.lastConnection?.getTime() || 0);

        return comparison * directionMultiplier;
    });
};

// LOGO & ICON CROPPING UTILS

export const MIN_LOGO_WIDTH = 840;
export const MIN_LOGO_HEIGHT = 512;

export const MIN_ICON_WIDTH_AND_HEIGHT = 512;

export const HOTEL_IMAGE_TYPE_KEY_ICONS: Record<string, string> = {
    AMENITIES: 'mdi:room-service-outline',
    OUTSIDE: 'mdi:tree-outline',
    RESTAURANT: 'mdi:silverware-fork-knife',
    MAIN: 'mdi:sofa-outline',
    BEDROOM: 'mdi:bed-outline',
};

export const IMAGE_TYPE_KEY_ROOM = 'BEDROOM';
export const IMAGE_MAX_SIZE_ALLOWED = 10_485_760; // 10MB = 10 * 1024 *1024 bytes = 26214400
export const IMAGE_MAX_SIZE_ALLOWED_MB = 10;
export const MIN_IMAGE_WIDTH = 840;
export const MIN_IMAGE_HEIGHT = 460;
export const IMAGE_TO_UPLOAD_INITIAL_STATE: LocalHotelImageState = {
    Id: '',
    IdCategory: 0,
    Category: '',
    CategoryKey: '',
    Url: '',
    Caption: '',
    Feature: false,
    Published: false,
    IdHotelRoomType: 0,
    IdSupplierHotelRoomType: 0,
    __isAdded: false,
    __isChanged: false,
    Deleted: false,
    File: null,
};

export const COMPETITORS_ZITONEO_SCRAPING_EXECUTION_TYPE_KEY = 'COMPETITORS';
